import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAvatar } from '@/features/profile/contexts/AvatarContext';
import { useAvatars } from '../hooks/useAvatars';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorDisplay from '@/components/ui/ErrorDisplay';
import { updateMe } from '@/services/authService';
import { Button } from '@/components/ui';

interface AvatarSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void; // Add onSave callback
}

export const AvatarSelector = ({ isOpen, onClose, onSave }: AvatarSelectorProps) => {
  const { selectedAvatar, setSelectedAvatar, refreshAvatar } = useAvatar();
  const { avatars, loading, error, refetch } = useAvatars();
  const [tempAvatar, setTempAvatar] = useState(selectedAvatar);
  const [saving, setSaving] = useState(false);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Prevent scrolling on mobile devices
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = '0';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);

  const handleSelect = async () => {
    if (!tempAvatar) return;

    setSaving(true);
    try {
      // Save avatar to backend
      await updateMe({ avatar_url: tempAvatar });

      // Refresh avatar from backend to ensure consistency
      await refreshAvatar();

      console.log('✅ Avatar updated successfully');
      onSave(); // Call onSave instead of onClose directly
    } catch (error) {
      console.error('❌ Failed to update avatar:', error);
      // Still update local state as fallback
      setSelectedAvatar(tempAvatar);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        aria-hidden={!isOpen}
        className={cn(
          'fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4',
          'transition-opacity duration-200 ease-out',
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={onClose}
      >
          <div
            className={cn(
              'relative w-full max-w-md max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col',
              'transition-[transform,opacity] duration-200 ease-out',
              isOpen ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-3 scale-95 opacity-0'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-0">
              <h2 className="text-lg font-bold text-gray-800">انتخاب آواتار</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content with scrolling */}
            <div className="flex-1 overflow-y-auto px-6 py-6 scrollbar-hide">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : error ? (
                <ErrorDisplay
                  message={error}
                  onRetry={refetch}
                />
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {avatars.map((avatar) => (
                    <div
                      key={avatar.id}
                      onClick={() => setTempAvatar(avatar.url)}
                      className={cn(
                        'flex cursor-pointer items-center justify-center rounded-xl border-2 p-2 transition-all overflow-hidden',
                        tempAvatar === avatar.url
                          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
                          : 'border-gray-200 bg-gray-50 hover:border-primary-300',
                      )}
                    >
                      <img
                        src={avatar.url}
                        alt={avatar.name}
                        className="w-16 h-16 rounded-lg object-cover"
                        onError={(e) => {
                          // Fallback if image fails to load
                          const target = e.currentTarget;
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) {
                            target.style.display = 'none';
                            fallback.style.display = 'flex';
                          }
                        }}
                      />
                      <div
                        className="w-16 h-16 rounded-lg bg-gray-300 flex items-center justify-center text-gray-600 text-xs font-medium hidden"
                      >
                        {avatar.name.slice(0, 2)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fixed footer with buttons */}
            <div className="p-6 pt-0 border-t border-gray-100 mt-auto">
              <div className="flex flex-col gap-3 pt-6">
                <Button
                  onClick={handleSelect}
                  disabled={saving || !tempAvatar}
                  className="w-full"
                >
                  {saving ? (
                    <div className="flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      در حال ذخیره...
                    </div>
                  ) : (
                    'تایید'
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={onClose}
                  disabled={saving}
                  className="w-full"
                >
                  انصراف
                </Button>
              </div>
            </div>
          </div>
      </div>
    </>
  );
};
